// @flow
import React from 'react'
import styled, { css } from 'styled-components'
import { Input, Radio } from 'antd'
import Path from 'routes/path'
import { fontSize, spacing } from 'frontend_core/dist/styles/variables'
import { mediaMin } from 'frontend_core/dist/styles/media'
import Colors from 'components/styles/colors'
import Button from 'components/atoms/Button'
import LinkText from 'components/atoms/LinkText'
import Icon from 'components/atoms/Icon'
import SelectItem from 'components/atoms/SelectItem'
import InputContainer from 'components/molecules/InputContainer'
import FormItem from 'components/molecules/FormItem'
import RadioList from 'components/molecules/RadioList'
import RadioListCreditcard from 'components/molecules/RadioListCreditcard'
import FormSectionTitle from 'components/molecules/FormSectionTitle'
import PartsContainer from 'components/molecules/PartsContainer'
import { imagePath } from 'frontend_core/dist/helpers/image'

const SubSection = styled.div`
  margin-top: ${spacing.medium_2}px;
  border-top: 1px solid ${Colors.grayE};
`
const PayMethodItem = styled.div`
  padding: ${spacing.medium__15}px 0;
  ${mediaMin.phone`
    padding: ${spacing.medium_2}px 0;
  `} :not(:first-of-type) {
    border-top: 1px solid ${Colors.grayE};
  }

  :last-of-type {
    border-bottom: 1px solid ${Colors.grayE};
  }

  ${({ isBorder }) =>
    isBorder &&
    css`
      border-top: 1px solid ${Colors.grayE};
    `};
`
const PayMethodItemTitle = styled.p`
  font-size: ${fontSize.small_3}px;
`
const PayMethodItemContent = styled.div`
  padding-top: ${spacing.medium__15}px;
  margin-top: ${spacing.medium__15}px;
  border-top: 1px dashed ${Colors.grayE};
  ${mediaMin.phone`
    padding-top: ${spacing.medium_2}px;
    margin-top: ${spacing.medium}px;
    margin-top: ${spacing.medium_2}px;
  `};
`
const ButtonWrap = styled.div`
  margin-top: ${spacing.small_3}px;
  ${mediaMin.phone`
  margin-top: ${spacing.medium}px;
  `};
`
const SelectCardBlockContainer = styled.div`
  display: none;
  padding: ${spacing.medium_3}px 0 ${spacing.small_3}px;
  ${({ isOpen }) =>
    isOpen &&
    css`
      display: block;
    `};
`
const SelectCardBlock = styled.div`
  ${mediaMin.phone`
    display: table;
  `};
  :not(:first-of-type) {
    margin-top: ${spacing.medium_3}px;
  }
`
const SelectCardBlockHead = styled.div`
  font-size: ${fontSize.small_3}px;
  font-weight: bold;
  ${mediaMin.phone`
    display: table-cell;
    width: 160px;
    vertical-align: top;
  `};
`
const SelectCardBlockBody = styled.div`
  margin-top: ${spacing.small_3}px;
  ${mediaMin.phone`
    display: table-cell;
    vertical-align: top;
    width: 300px;
  `};
`
const CardImage = styled.img`
  height: 24px;
  width: auto;

  :not(:first-of-type) {
    margin-left: ${spacing.small}px;
  }
`
const GuideText = styled.pre`
  color: ${Colors.gray9};
  font-size: ${fontSize.small_2}px;
  line-height: 1.27;
  white-space: pre-wrap;
`
const ExpirationDateFormBlock = styled.div`
  display: table;
  table-layout: fixed;
  width: 100%;
`
const ExpirationDateFormBlockForm = styled.div`
  display: table-cell;
  vertical-align: middle;
`
const ExpirationDateFormBlockUnit = styled.div`
  display: table-cell;
  width: 18px;
  text-align: center;
  vertical-align: middle;
`
const FormToGuide = styled.div`
  margin-top: ${spacing.small_3}px;
  ${mediaMin.phone`
    margin-top: ${spacing.small_2}px;
  `};
`
const SelectCardBlockButtonContaier = styled.div`
  ${mediaMin.phone`
    max-width: 300px;
  `};
`
const SelectCardBlockButtonWrapper = styled.div`
  display: inline-block;
  width: 50%;

  &:first-of-type {
    padding-right: ${spacing.small}px;
  }

  &:nth-of-type(2n) {
    padding-left: ${spacing.small}px;
  }
`

export type PropTypes = {
  selectedCardId: number,
  radioListCard: Array<{
    number: string | number,
    accountHolder: string,
    expirationDate: string,
    value: string | number,
    srcCardImage?: string,
  }>,
  optionListMonth: Array<{
    label: string,
    value: number | string,
  }>,
  optionListYear: Array<{
    label: string,
    value: number | string,
  }>,
  isOpenCreditCard?: boolean,
  cardNumber: Array<string>,
  cardNumberErrors: Array<string>,
  cardHolderName: string,
  cardExpireMonth: string,
  cardExpireYear: string,
  onChangePaymentCard: Function,
  onChangeCardNumber: Function,
  onChangeCardHolderName: Function,
  onChangeCardExpire: Function,
  onClickOpenCreditCardForm: Function,
  onClickAddCreditCard: Function,
}

export default function SelectCreditCardSection(props: PropTypes) {
  const RadioGroup = Radio.Group

  return (
    <SubSection>
      <PayMethodItem>
        <PayMethodItemTitle>
          {i18n.t('page.shared.creditcard')}
        </PayMethodItemTitle>
        <PayMethodItemContent>
          <RadioListCreditcard
            name="paymethod"
            radioList={props.radioListCard}
            widthType="full"
            selectedValue={props.selectedCardId}
            onChange={props.onChangePaymentCard}
          />
        </PayMethodItemContent>
        <PayMethodItemContent>
          <LinkText onClick={props.onClickOpenCreditCardForm}>
            {i18n.t('page.dashboard.reservation.selectPayMethod.addCreditcard')}
            <Icon
              className={
                props.isOpenCreditCard ? 'fa fa-angle-up' : 'fa fa-angle-down'
              }
              positionType="right"
            />
          </LinkText>
          <SelectCardBlockContainer isOpen={props.isOpenCreditCard}>
            <SelectCardBlock>
              <SelectCardBlockHead>
                {i18n.t(
                  'page.dashboard.reservation.selectPayMethod.availableCard',
                )}
              </SelectCardBlockHead>
              <SelectCardBlockBody>
                <CardImage src={imagePath('www/common/card_visa.png')} />
                <CardImage src={imagePath('www/common/card_master.png')} />
                <CardImage src={imagePath('www/common/card_jcb.png')} />
                <CardImage src={imagePath('www/common/card_amex.png')} />
                <CardImage src={imagePath('www/common/card_diners.png')} />
                <CardImage src={imagePath('www/common/card_discover.png')} />
              </SelectCardBlockBody>
            </SelectCardBlock>
            <SelectCardBlock>
              <SelectCardBlockHead>
                {i18n.t(
                  'page.dashboard.reservation.selectPayMethod.cardNumber',
                )}
              </SelectCardBlockHead>
              <SelectCardBlockBody>
                <FormItem
                  renderInput={() => (
                    <PartsContainer width="25%" spacing={spacing.tiny}>
                      <Input
                        size="large"
                        id="cardNumber0"
                        placeholder="0000"
                        value={props.cardNumber[0] || ''}
                        onChange={e =>
                          props.onChangeCardNumber(e.target.value, 0)
                        }
                      />
                      <Input
                        size="large"
                        id="cardNumber1"
                        placeholder="0000"
                        value={props.cardNumber[1] || ''}
                        onChange={e =>
                          props.onChangeCardNumber(e.target.value, 1)
                        }
                      />
                      <Input
                        size="large"
                        id="cardNumber2"
                        placeholder="0000"
                        value={props.cardNumber[2] || ''}
                        onChange={e =>
                          props.onChangeCardNumber(e.target.value, 2)
                        }
                      />
                      <Input
                        size="large"
                        id="cardNumber3"
                        placeholder="0000"
                        value={props.cardNumber[3] || ''}
                        onChange={e =>
                          props.onChangeCardNumber(e.target.value, 3)
                        }
                      />
                    </PartsContainer>
                  )}
                  errorTexts={props.cardNumberErrors}
                />
              </SelectCardBlockBody>
            </SelectCardBlock>
            <SelectCardBlock>
              <SelectCardBlockHead>
                {i18n.t(
                  'page.dashboard.reservation.selectPayMethod.accountHolder',
                )}
              </SelectCardBlockHead>
              <SelectCardBlockBody>
                <GuideText>
                  {i18n.t(
                    'page.dashboard.reservation.selectPayMethod.accountHolderGuide',
                  )}
                </GuideText>
                <FormToGuide>
                  <FormItem
                    renderInput={() => (
                      <Input
                        size="large"
                        placeholder="TARO SPACE"
                        value={props.cardHolderName}
                        onChange={e =>
                          props.onChangeCardHolderName(e.target.value)
                        }
                      />
                    )}
                  />
                </FormToGuide>
              </SelectCardBlockBody>
            </SelectCardBlock>
            <SelectCardBlock>
              <SelectCardBlockHead>
                {i18n.t(
                  'page.dashboard.reservation.selectPayMethod.expirationDate',
                )}
              </SelectCardBlockHead>
              <SelectCardBlockBody>
                <ExpirationDateFormBlock>
                  <ExpirationDateFormBlockForm>
                    <FormItem
                      renderInput={() => (
                        <SelectItem
                          selectProps={{
                            size: 'large',
                            defaultValue: props.cardExpireMonth,
                            onChange: value =>
                              props.onChangeCardExpire('month', value),
                          }}
                          optionList={props.optionListMonth}
                        />
                      )}
                    />
                  </ExpirationDateFormBlockForm>
                  <ExpirationDateFormBlockUnit>/</ExpirationDateFormBlockUnit>
                  <ExpirationDateFormBlockForm>
                    <FormItem
                      renderInput={() => (
                        <SelectItem
                          selectProps={{
                            size: 'large',
                            defaultValue: props.cardExpireYear,
                            onChange: value =>
                              props.onChangeCardExpire('year', value),
                          }}
                          optionList={props.optionListYear}
                        />
                      )}
                    />
                  </ExpirationDateFormBlockForm>
                </ExpirationDateFormBlock>
              </SelectCardBlockBody>
            </SelectCardBlock>
            <SelectCardBlock>
              <SelectCardBlockHead>
                {i18n.t(
                  'page.dashboard.reservation.selectPayMethod.securityCode',
                )}
              </SelectCardBlockHead>
              <SelectCardBlockBody>
                <GuideText>
                  {i18n.t(
                    'page.dashboard.reservation.selectPayMethod.securityCodeGuide',
                  )}
                </GuideText>
                <FormToGuide>
                  <FormItem
                    renderInput={() => (
                      <InputContainer width="140px">
                        <Input
                          size="large"
                          placeholder="000"
                          value={props.cvc}
                          onChange={e => props.onChangeCvc(e.target.value)}
                        />
                      </InputContainer>
                    )}
                    errorTexts={props.cvcErrors}
                  />
                </FormToGuide>
              </SelectCardBlockBody>
            </SelectCardBlock>
            <SelectCardBlock>
              <SelectCardBlockHead />
              <SelectCardBlockBody>
                <SelectCardBlockButtonContaier>
                  <SelectCardBlockButtonWrapper>
                    <Button
                      tag="button"
                      colorType="gray"
                      buttonType="small"
                      fullWidthSp
                      fullWidthPc
                      onClick={props.onClickOpenCreditCardForm}
                    >
                      {i18n.t('page.shared.cancel')}
                    </Button>
                  </SelectCardBlockButtonWrapper>
                  <SelectCardBlockButtonWrapper>
                    <Button
                      tag="button"
                      colorType="blue"
                      buttonType="small"
                      fullWidthSp
                      fullWidthPc
                      onClick={props.onClickAddCreditCard}
                    >
                      {i18n.t('page.shared.add')}
                    </Button>
                  </SelectCardBlockButtonWrapper>
                </SelectCardBlockButtonContaier>
              </SelectCardBlockBody>
            </SelectCardBlock>
          </SelectCardBlockContainer>
        </PayMethodItemContent>
      </PayMethodItem>
    </SubSection>
  )
}



// WEBPACK FOOTER //
// ./src/components/organisms/SelectCreditCardSection/index.jsx